import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    DollarSign,
    TrendingUp,
    TrendingDown,
    Calendar,
    Download,
    FileSpreadsheet,
    FileText,
    Package,
    Award,
    ShoppingBag,
    PieChart,
    BarChart3,
    ArrowUpRight,
    ArrowDownRight,
    RefreshCw,
    Loader2,
    Filter,
    History,
    CalendarDays,
    ChevronUp,
    ChevronDown,
    Minus,
    Receipt,
    Wallet,
    Target
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FinancialPage = () => {
    const [loading, setLoading] = useState(true);
    const [dashboard, setDashboard] = useState(null);
    const [history, setHistory] = useState(null);
    const [productsReport, setProductsReport] = useState(null);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [productsDialogOpen, setProductsDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('atual');
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    // Export form
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');
    const [exportFormat, setExportFormat] = useState('csv');
    const [exporting, setExporting] = useState(false);

    const fetchDashboard = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/financial/dashboard`);
            setDashboard(response.data);
        } catch (error) {
            console.error('Error fetching dashboard:', error);
            toast.error('Erro ao carregar dashboard financeiro');
        }
    }, []);

    const fetchHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const response = await axios.get(`${API_URL}/api/financial/history`);
            setHistory(response.data);
        } catch (error) {
            console.error('Error fetching history:', error);
            toast.error('Erro ao carregar histórico financeiro');
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    const fetchProductsReport = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/financial/products`);
            setProductsReport(response.data);
            setProductsDialogOpen(true);
        } catch (error) {
            console.error('Error fetching products report:', error);
            toast.error('Erro ao carregar relatório de produtos');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await fetchDashboard();
            setLoading(false);
        };
        loadData();
        
        // Set default export dates
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        setExportStartDate(firstDay.toISOString().split('T')[0]);
        setExportEndDate(now.toISOString().split('T')[0]);
    }, [fetchDashboard]);

    // Load history when switching to historico tab
    useEffect(() => {
        if (activeTab === 'historico' && !history) {
            fetchHistory();
        }
    }, [activeTab, history, fetchHistory]);

    const handleExport = async () => {
        if (!exportStartDate || !exportEndDate) {
            toast.error('Selecione o período');
            return;
        }

        setExporting(true);
        try {
            const response = await axios.get(`${API_URL}/api/financial/export`, {
                params: {
                    start_date: exportStartDate,
                    end_date: exportEndDate,
                    format: exportFormat
                },
                responseType: exportFormat === 'csv' ? 'blob' : 'json'
            });

            if (exportFormat === 'csv') {
                // Download CSV file
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `relatorio_financeiro_${exportStartDate}_${exportEndDate}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('Relatório CSV exportado!');
            } else {
                // Download JSON
                const dataStr = JSON.stringify(response.data, null, 2);
                const url = window.URL.createObjectURL(new Blob([dataStr], { type: 'application/json' }));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `relatorio_financeiro_${exportStartDate}_${exportEndDate}.json`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('Relatório JSON exportado!');
            }
            
            setExportDialogOpen(false);
        } catch (error) {
            console.error('Error exporting report:', error);
            toast.error('Erro ao exportar relatório');
        } finally {
            setExporting(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
    };

    const formatPercent = (value) => {
        return `${(value || 0).toFixed(1)}%`;
    };

    // Variation indicator component
    const VariationIndicator = ({ value, size = 'sm' }) => {
        if (value === 0 || value === undefined || value === null) {
            return (
                <span className={`flex items-center gap-0.5 text-gray-500 ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
                    <Minus className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
                    0%
                </span>
            );
        }
        const isPositive = value > 0;
        return (
            <span className={`flex items-center gap-0.5 ${isPositive ? 'text-emerald-500' : 'text-red-500'} ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
                {isPositive ? (
                    <ChevronUp className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
                ) : (
                    <ChevronDown className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />
                )}
                {Math.abs(value).toFixed(1)}%
            </span>
        );
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-rr-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-4 lg:space-y-6 animate-fade-in-up" data-testid="financial-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white">Financeiro</h1>
                    <p className="text-gray-400 text-sm lg:text-base mt-1">Dashboard de lucros e análises</p>
                </div>
                <div className="flex items-center gap-2 lg:gap-3">
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-[#262626] text-gray-400 h-8 lg:h-9"
                        onClick={() => {
                            fetchDashboard();
                            if (history) fetchHistory();
                        }}
                    >
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="border-[#262626] text-gray-400 h-8 lg:h-9"
                        onClick={() => setExportDialogOpen(true)}
                    >
                        <Download className="w-4 h-4 lg:mr-2" />
                        <span className="hidden lg:inline">Exportar</span>
                    </Button>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-[#1a1a1a] h-10 lg:h-11">
                    <TabsTrigger 
                        value="atual" 
                        className="data-[state=active]:bg-rr-yellow-500 data-[state=active]:text-black text-gray-400"
                    >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        <span className="hidden sm:inline">Período </span>Atual
                    </TabsTrigger>
                    <TabsTrigger 
                        value="historico"
                        className="data-[state=active]:bg-rr-yellow-500 data-[state=active]:text-black text-gray-400"
                    >
                        <History className="w-4 h-4 mr-2" />
                        Histórico
                    </TabsTrigger>
                </TabsList>

                {/* Current Period Tab */}
                <TabsContent value="atual" className="space-y-4 lg:space-y-6 mt-4">
                    {/* Period Summary Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
                        {/* Daily */}
                        <Card className="bg-[#121212] border-[#262626]">
                            <CardHeader className="pb-2 p-3 lg:p-6 lg:pb-2">
                                <CardTitle className="text-xs lg:text-sm font-medium text-gray-400 flex items-center gap-2">
                                    <Calendar className="w-3 h-3 lg:w-4 lg:h-4" />
                                    Hoje
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 lg:space-y-3 p-3 lg:p-6 pt-0">
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <p className="text-[10px] lg:text-xs text-gray-500 uppercase">Receita</p>
                                        <p className="text-lg lg:text-2xl font-bold text-white">{formatCurrency(dashboard?.summary?.daily?.revenue)}</p>
                                    </div>
                                    <Badge className="bg-emerald-500/20 text-emerald-500 text-[10px] lg:text-xs">
                                        {dashboard?.summary?.daily?.count || 0} vendas
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between p-2 lg:p-3 bg-emerald-500/5 rounded-md border border-emerald-500/20">
                                    <span className="text-xs lg:text-sm text-gray-400">Lucro Bruto</span>
                                    <span className="text-base lg:text-lg font-bold text-emerald-500">{formatCurrency(dashboard?.summary?.daily?.profit)}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 lg:p-3 bg-red-500/5 rounded-md border border-red-500/20">
                                    <span className="text-xs lg:text-sm text-gray-400">Despesas</span>
                                    <span className="text-base lg:text-lg font-bold text-red-500">-{formatCurrency(dashboard?.summary?.daily?.expenses)}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 lg:p-3 bg-rr-yellow-500/10 rounded-md border border-rr-yellow-500/30">
                                    <span className="text-xs lg:text-sm text-gray-400 font-medium">Lucro Líquido</span>
                                    <span className={`text-base lg:text-lg font-bold ${(dashboard?.summary?.daily?.net_profit || 0) >= 0 ? 'text-rr-yellow-500' : 'text-red-500'}`}>
                                        {formatCurrency(dashboard?.summary?.daily?.net_profit)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-xs lg:text-sm">
                                    <span className="text-gray-500">Ticket Médio</span>
                                    <span className="text-white">{formatCurrency(dashboard?.summary?.daily?.avg_ticket)}</span>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Weekly */}
                        <Card className="bg-[#121212] border-[#262626]">
                            <CardHeader className="pb-2 p-3 lg:p-6 lg:pb-2">
                                <CardTitle className="text-xs lg:text-sm font-medium text-gray-400 flex items-center gap-2">
                                    <Calendar className="w-3 h-3 lg:w-4 lg:h-4" />
                                    Esta Semana
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 lg:space-y-3 p-3 lg:p-6 pt-0">
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <p className="text-[10px] lg:text-xs text-gray-500 uppercase">Receita</p>
                                        <p className="text-lg lg:text-2xl font-bold text-white">{formatCurrency(dashboard?.summary?.weekly?.revenue)}</p>
                                    </div>
                                    <Badge className="bg-blue-500/20 text-blue-500 text-[10px] lg:text-xs">
                                        {dashboard?.summary?.weekly?.count || 0} vendas
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between p-2 lg:p-3 bg-blue-500/5 rounded-md border border-blue-500/20">
                                    <span className="text-xs lg:text-sm text-gray-400">Lucro Bruto</span>
                                    <span className="text-base lg:text-lg font-bold text-blue-500">{formatCurrency(dashboard?.summary?.weekly?.profit)}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 lg:p-3 bg-red-500/5 rounded-md border border-red-500/20">
                                    <span className="text-xs lg:text-sm text-gray-400">Despesas</span>
                                    <span className="text-base lg:text-lg font-bold text-red-500">-{formatCurrency(dashboard?.summary?.weekly?.expenses)}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 lg:p-3 bg-rr-yellow-500/10 rounded-md border border-rr-yellow-500/30">
                                    <span className="text-xs lg:text-sm text-gray-400 font-medium">Lucro Líquido</span>
                                    <span className={`text-base lg:text-lg font-bold ${(dashboard?.summary?.weekly?.net_profit || 0) >= 0 ? 'text-rr-yellow-500' : 'text-red-500'}`}>
                                        {formatCurrency(dashboard?.summary?.weekly?.net_profit)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">Ticket Médio</span>
                                    <span className="text-white">{formatCurrency(dashboard?.summary?.weekly?.avg_ticket)}</span>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Monthly */}
                        <Card className="bg-[#121212] border-rr-yellow-500/30">
                            <CardHeader className="pb-2 p-3 lg:p-6 lg:pb-2">
                                <CardTitle className="text-xs lg:text-sm font-medium text-gray-400 flex items-center gap-2">
                                    <Calendar className="w-3 h-3 lg:w-4 lg:h-4" />
                                    Este Mês
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2 lg:space-y-3 p-3 lg:p-6 pt-0">
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <p className="text-[10px] lg:text-xs text-gray-500 uppercase">Receita</p>
                                        <p className="text-lg lg:text-2xl font-bold text-white">{formatCurrency(dashboard?.summary?.monthly?.revenue)}</p>
                                    </div>
                                    <Badge className="bg-rr-yellow-500/20 text-rr-yellow-500 text-[10px] lg:text-xs">
                                        {dashboard?.summary?.monthly?.count || 0} vendas
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between p-2 lg:p-3 bg-emerald-500/5 rounded-md border border-emerald-500/20">
                                    <span className="text-xs lg:text-sm text-gray-400">Lucro Bruto</span>
                                    <span className="text-base lg:text-lg font-bold text-emerald-500">{formatCurrency(dashboard?.summary?.monthly?.profit)}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 lg:p-3 bg-red-500/5 rounded-md border border-red-500/20">
                                    <div>
                                        <span className="text-xs lg:text-sm text-gray-400">Despesas</span>
                                        {dashboard?.summary?.monthly?.expenses_pending > 0 && (
                                            <span className="text-[10px] text-orange-500 block">
                                                ({formatCurrency(dashboard?.summary?.monthly?.expenses_pending)} pendentes)
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-base lg:text-lg font-bold text-red-500">-{formatCurrency(dashboard?.summary?.monthly?.expenses)}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 lg:p-3 bg-rr-yellow-500/10 rounded-md border border-rr-yellow-500/30">
                                    <span className="text-xs lg:text-sm text-gray-400 font-medium">Lucro Líquido</span>
                                    <span className={`text-base lg:text-lg font-bold ${(dashboard?.summary?.monthly?.net_profit || 0) >= 0 ? 'text-rr-yellow-500' : 'text-red-500'}`}>
                                        {formatCurrency(dashboard?.summary?.monthly?.net_profit)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-500">Ticket Médio</span>
                                    <span className="text-white">{formatCurrency(dashboard?.summary?.monthly?.avg_ticket)}</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Daily Profit Chart */}
                    <Card className="bg-[#121212] border-[#262626]">
                        <CardHeader>
                            <CardTitle className="text-base lg:text-lg font-medium text-white flex items-center gap-2">
                                <BarChart3 className="w-5 h-5 text-rr-yellow-500" />
                                Evolução (7 dias)
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-48 lg:h-64">
                                <div className="flex items-end justify-between h-full gap-2 lg:gap-4">
                                    {dashboard?.daily_chart?.map((day, idx) => (
                                        <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                                            <div className="w-full flex flex-col gap-1 items-center">
                                                {/* Revenue bar */}
                                                <div 
                                                    className="w-full bg-blue-500/30 rounded-t-sm min-h-[4px]"
                                                    style={{ 
                                                        height: `${Math.max(4, (day.revenue / Math.max(...(dashboard?.daily_chart?.map(d => d.revenue) || [1]))) * 120)}px` 
                                                    }}
                                                />
                                                {/* Profit bar */}
                                                <div 
                                                    className="w-3/4 bg-emerald-500 rounded-sm min-h-[4px]"
                                                    style={{ 
                                                        height: `${Math.max(4, (day.profit / Math.max(...(dashboard?.daily_chart?.map(d => d.revenue) || [1]))) * 120)}px` 
                                                    }}
                                                />
                                            </div>
                                            <div className="text-center">
                                                <p className="text-[10px] lg:text-xs text-gray-500">{day.date}</p>
                                                <p className="text-[10px] lg:text-xs text-emerald-500 font-medium">{formatCurrency(day.profit)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-center gap-6 mt-4 text-xs">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-blue-500/30 rounded" />
                                    <span className="text-gray-400">Receita</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-emerald-500 rounded" />
                                    <span className="text-gray-400">Lucro</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Top Products and Categories */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                        {/* Most Profitable Products */}
                        <Card className="bg-[#121212] border-[#262626]">
                            <CardHeader className="flex flex-row items-center justify-between p-3 lg:p-6">
                                <CardTitle className="text-sm lg:text-base font-medium text-white flex items-center gap-2">
                                    <Award className="w-4 h-4 lg:w-5 lg:h-5 text-rr-yellow-500" />
                                    Mais Lucrativos
                                </CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-rr-yellow-500 h-7 lg:h-8 text-xs lg:text-sm"
                                    onClick={fetchProductsReport}
                                >
                                    Ver Todos
                                </Button>
                            </CardHeader>
                            <CardContent className="p-3 lg:p-6 pt-0">
                                <ScrollArea className="h-48 lg:h-64">
                                    <div className="space-y-2">
                                        {dashboard?.most_profitable?.slice(0, 5).map((product, idx) => (
                                            <div key={idx} className="flex items-center justify-between p-2 bg-[#1a1a1a] rounded-md">
                                                <div className="flex items-center gap-2 lg:gap-3">
                                                    <span className="text-rr-yellow-500 font-bold text-xs lg:text-sm">{idx + 1}º</span>
                                                    <div>
                                                        <p className="text-white text-xs lg:text-sm font-medium truncate max-w-[120px] lg:max-w-[200px]">{product.name}</p>
                                                        <p className="text-gray-500 text-[10px] lg:text-xs">{product.quantity} vendidos</p>
                                                    </div>
                                                </div>
                                                <span className="text-emerald-500 font-bold text-xs lg:text-sm">{formatCurrency(product.profit)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </ScrollArea>
                            </CardContent>
                        </Card>

                        {/* Category Performance */}
                        <Card className="bg-[#121212] border-[#262626]">
                            <CardHeader className="p-3 lg:p-6">
                                <CardTitle className="text-sm lg:text-base font-medium text-white flex items-center gap-2">
                                    <PieChart className="w-4 h-4 lg:w-5 lg:h-5 text-rr-yellow-500" />
                                    Lucro por Categoria
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 lg:p-6 pt-0">
                                <ScrollArea className="h-48 lg:h-64">
                                    <div className="space-y-2">
                                        {Object.entries(dashboard?.category_profits || {})
                                            .sort(([,a], [,b]) => b.profit - a.profit)
                                            .slice(0, 6)
                                            .map(([category, data], idx) => (
                                                <div key={idx} className="flex items-center justify-between p-2 bg-[#1a1a1a] rounded-md">
                                                    <div>
                                                        <p className="text-white text-xs lg:text-sm font-medium">{category}</p>
                                                        <p className="text-gray-500 text-[10px] lg:text-xs">{data.count} itens • {formatCurrency(data.revenue)} receita</p>
                                                    </div>
                                                    <span className="text-emerald-500 font-bold text-xs lg:text-sm">{formatCurrency(data.profit)}</span>
                                                </div>
                                            ))}
                                    </div>
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Expense Summary by Tag */}
                    {dashboard?.expense_by_tag && Object.keys(dashboard.expense_by_tag).length > 0 && (
                        <Card className="bg-[#121212] border-[#262626]">
                            <CardHeader className="p-3 lg:p-6">
                                <CardTitle className="text-sm lg:text-base font-medium text-white flex items-center gap-2">
                                    <Receipt className="w-4 h-4 lg:w-5 lg:h-5 text-red-500" />
                                    Despesas por Categoria (Este Mês)
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 lg:p-6 pt-0">
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                    {Object.entries(dashboard.expense_by_tag).map(([tagName, data], idx) => (
                                        <div key={idx} className="p-3 bg-[#1a1a1a] rounded-lg border-l-4" style={{ borderColor: data.color }}>
                                            <p className="text-gray-400 text-xs truncate">{tagName}</p>
                                            <p className="text-white font-bold text-sm lg:text-base">{formatCurrency(data.amount)}</p>
                                            <p className="text-gray-500 text-[10px]">{data.count} despesa(s)</p>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* History Tab */}
                <TabsContent value="historico" className="space-y-4 lg:space-y-6 mt-4">
                    {loadingHistory ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="w-12 h-12 border-4 border-rr-yellow-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Accumulated Totals */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/30">
                                    <CardContent className="p-3 lg:p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <DollarSign className="w-4 h-4 lg:w-5 lg:h-5 text-blue-500" />
                                            <span className="text-xs lg:text-sm text-gray-400">Receita Total</span>
                                        </div>
                                        <p className="text-lg lg:text-2xl font-bold text-white">
                                            {formatCurrency(history?.accumulated?.total_revenue)}
                                        </p>
                                        <p className="text-[10px] lg:text-xs text-gray-500 mt-1">
                                            {history?.accumulated?.total_sales || 0} vendas totais
                                        </p>
                                    </CardContent>
                                </Card>

                                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/30">
                                    <CardContent className="p-3 lg:p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-500" />
                                            <span className="text-xs lg:text-sm text-gray-400">Lucro Bruto</span>
                                        </div>
                                        <p className="text-lg lg:text-2xl font-bold text-emerald-500">
                                            {formatCurrency(history?.accumulated?.total_profit)}
                                        </p>
                                        <p className="text-[10px] lg:text-xs text-gray-500 mt-1">
                                            Margem: {history?.accumulated?.overall_margin || 0}%
                                        </p>
                                    </CardContent>
                                </Card>

                                <Card className="bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/30">
                                    <CardContent className="p-3 lg:p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Receipt className="w-4 h-4 lg:w-5 lg:h-5 text-red-500" />
                                            <span className="text-xs lg:text-sm text-gray-400">Despesas</span>
                                        </div>
                                        <p className="text-lg lg:text-2xl font-bold text-red-500">
                                            -{formatCurrency(history?.accumulated?.total_expenses)}
                                        </p>
                                        <p className="text-[10px] lg:text-xs text-gray-500 mt-1">
                                            Todas as despesas pagas
                                        </p>
                                    </CardContent>
                                </Card>

                                <Card className={`bg-gradient-to-br ${(history?.accumulated?.total_net_profit || 0) >= 0 ? 'from-gold-500/10 to-gold-500/5 border-rr-yellow-500/30' : 'from-red-500/10 to-red-500/5 border-red-500/30'}`}>
                                    <CardContent className="p-3 lg:p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Wallet className="w-4 h-4 lg:w-5 lg:h-5" style={{ color: (history?.accumulated?.total_net_profit || 0) >= 0 ? '#eab308' : '#ef4444' }} />
                                            <span className="text-xs lg:text-sm text-gray-400">Lucro Líquido</span>
                                        </div>
                                        <p className={`text-lg lg:text-2xl font-bold ${(history?.accumulated?.total_net_profit || 0) >= 0 ? 'text-rr-yellow-500' : 'text-red-500'}`}>
                                            {formatCurrency(history?.accumulated?.total_net_profit)}
                                        </p>
                                        <Badge className={`mt-1 text-[10px] ${history?.status === 'lucro' ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
                                            {history?.status === 'lucro' ? '✓ Empresa Lucrativa' : '⚠ Em Prejuízo'}
                                        </Badge>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Year Summary */}
                            {history?.years && history.years.length > 0 && (
                                <Card className="bg-[#121212] border-[#262626]">
                                    <CardHeader className="p-3 lg:p-6">
                                        <CardTitle className="text-sm lg:text-base font-medium text-white flex items-center gap-2">
                                            <Target className="w-4 h-4 lg:w-5 lg:h-5 text-rr-yellow-500" />
                                            Resumo Anual
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-3 lg:p-6 pt-0">
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {history.years.map((year) => (
                                                <div 
                                                    key={year.year} 
                                                    className={`p-4 rounded-lg border ${year.net_profit >= 0 ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}
                                                >
                                                    <div className="flex items-center justify-between mb-3">
                                                        <span className="text-xl lg:text-2xl font-bold text-white">{year.year}</span>
                                                        <Badge className={year.net_profit >= 0 ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}>
                                                            {year.net_profit >= 0 ? 'Lucro' : 'Prejuízo'}
                                                        </Badge>
                                                    </div>
                                                    <div className="space-y-2 text-sm">
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-400">Receita:</span>
                                                            <span className="text-white font-medium">{formatCurrency(year.revenue)}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-400">Lucro Bruto:</span>
                                                            <span className="text-emerald-500 font-medium">{formatCurrency(year.profit)}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-400">Despesas:</span>
                                                            <span className="text-red-500 font-medium">-{formatCurrency(year.expenses)}</span>
                                                        </div>
                                                        <div className="flex justify-between pt-2 border-t border-[#262626]">
                                                            <span className="text-gray-300 font-medium">Lucro Líquido:</span>
                                                            <span className={`font-bold ${year.net_profit >= 0 ? 'text-rr-yellow-500' : 'text-red-500'}`}>
                                                                {formatCurrency(year.net_profit)}
                                                            </span>
                                                        </div>
                                                        <div className="flex justify-between text-xs text-gray-500">
                                                            <span>{year.sales_count} vendas</span>
                                                            <span>Ticket: {formatCurrency(year.avg_ticket)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Monthly History Table */}
                            <Card className="bg-[#121212] border-[#262626]">
                                <CardHeader className="p-3 lg:p-6">
                                    <CardTitle className="text-sm lg:text-base font-medium text-white flex items-center gap-2">
                                        <History className="w-4 h-4 lg:w-5 lg:h-5 text-rr-yellow-500" />
                                        Histórico Mês a Mês
                                        <Badge className="bg-[#262626] text-gray-400 text-xs ml-2">
                                            {history?.months?.length || 0} meses
                                        </Badge>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-0 lg:p-6 lg:pt-0">
                                    {/* Desktop Table */}
                                    <div className="hidden lg:block">
                                        <ScrollArea className="h-[500px]">
                                            <table className="w-full">
                                                <thead className="sticky top-0 bg-[#121212]">
                                                    <tr className="border-b border-[#262626]">
                                                        <th className="text-left p-3 text-xs text-gray-400 font-medium">Mês</th>
                                                        <th className="text-right p-3 text-xs text-gray-400 font-medium">Receita</th>
                                                        <th className="text-right p-3 text-xs text-gray-400 font-medium">CPV</th>
                                                        <th className="text-right p-3 text-xs text-gray-400 font-medium">Lucro Bruto</th>
                                                        <th className="text-right p-3 text-xs text-gray-400 font-medium">Despesas</th>
                                                        <th className="text-right p-3 text-xs text-gray-400 font-medium">Lucro Líquido</th>
                                                        <th className="text-center p-3 text-xs text-gray-400 font-medium">Variação</th>
                                                        <th className="text-right p-3 text-xs text-gray-400 font-medium">Vendas</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {[...(history?.months || [])].reverse().map((month, idx) => (
                                                        <tr 
                                                            key={month.month_key} 
                                                            className={`border-b border-[#1a1a1a] hover:bg-[#1a1a1a] transition-colors ${idx === 0 ? 'bg-rr-yellow-500/5' : ''}`}
                                                        >
                                                            <td className="p-3">
                                                                <span className="text-white font-medium">{month.display}</span>
                                                                {idx === 0 && <Badge className="ml-2 bg-rr-yellow-500/20 text-rr-yellow-500 text-[10px]">Atual</Badge>}
                                                            </td>
                                                            <td className="text-right p-3 text-white">{formatCurrency(month.revenue)}</td>
                                                            <td className="text-right p-3 text-orange-400">{formatCurrency(month.cpv)}</td>
                                                            <td className="text-right p-3 text-emerald-500 font-medium">{formatCurrency(month.profit)}</td>
                                                            <td className="text-right p-3 text-red-500">-{formatCurrency(month.expenses)}</td>
                                                            <td className={`text-right p-3 font-bold ${month.net_profit >= 0 ? 'text-rr-yellow-500' : 'text-red-500'}`}>
                                                                {formatCurrency(month.net_profit)}
                                                            </td>
                                                            <td className="text-center p-3">
                                                                <VariationIndicator value={month.net_profit_variation} />
                                                            </td>
                                                            <td className="text-right p-3 text-gray-400">{month.sales_count}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </ScrollArea>
                                    </div>

                                    {/* Mobile Cards */}
                                    <div className="lg:hidden">
                                        <ScrollArea className="h-[500px]">
                                            <div className="space-y-2 p-3">
                                                {[...(history?.months || [])].reverse().map((month, idx) => (
                                                    <div 
                                                        key={month.month_key} 
                                                        className={`p-3 rounded-lg border ${idx === 0 ? 'bg-rr-yellow-500/5 border-rr-yellow-500/30' : 'bg-[#1a1a1a] border-[#262626]'}`}
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-white font-bold">{month.display}</span>
                                                                {idx === 0 && <Badge className="bg-rr-yellow-500/20 text-rr-yellow-500 text-[10px]">Atual</Badge>}
                                                            </div>
                                                            <VariationIndicator value={month.net_profit_variation} />
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">Receita:</span>
                                                                <span className="text-white">{formatCurrency(month.revenue)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">Vendas:</span>
                                                                <span className="text-gray-400">{month.sales_count}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">Lucro Bruto:</span>
                                                                <span className="text-emerald-500">{formatCurrency(month.profit)}</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span className="text-gray-500">Despesas:</span>
                                                                <span className="text-red-500">-{formatCurrency(month.expenses)}</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex justify-between mt-2 pt-2 border-t border-[#262626]">
                                                            <span className="text-gray-400 text-xs font-medium">Lucro Líquido:</span>
                                                            <span className={`font-bold text-sm ${month.net_profit >= 0 ? 'text-rr-yellow-500' : 'text-red-500'}`}>
                                                                {formatCurrency(month.net_profit)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </ScrollArea>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Monthly Evolution Chart */}
                            {history?.months && history.months.length > 1 && (
                                <Card className="bg-[#121212] border-[#262626]">
                                    <CardHeader className="p-3 lg:p-6">
                                        <CardTitle className="text-sm lg:text-base font-medium text-white flex items-center gap-2">
                                            <BarChart3 className="w-4 h-4 lg:w-5 lg:h-5 text-rr-yellow-500" />
                                            Evolução Mensal
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-3 lg:p-6 pt-0">
                                        <div className="h-48 lg:h-64">
                                            <div className="flex items-end justify-between h-full gap-1 lg:gap-2 overflow-x-auto pb-2">
                                                {history.months.slice(-12).map((month, idx) => {
                                                    const maxRevenue = Math.max(...history.months.slice(-12).map(m => m.revenue), 1);
                                                    const maxNetProfit = Math.max(...history.months.slice(-12).map(m => Math.abs(m.net_profit)), 1);
                                                    return (
                                                        <div key={idx} className="flex-1 min-w-[40px] lg:min-w-[60px] flex flex-col items-center gap-1">
                                                            <div className="w-full flex flex-col gap-1 items-center">
                                                                {/* Revenue bar */}
                                                                <div 
                                                                    className="w-full bg-blue-500/30 rounded-t-sm min-h-[4px]"
                                                                    style={{ 
                                                                        height: `${Math.max(4, (month.revenue / maxRevenue) * 100)}px` 
                                                                    }}
                                                                    title={`Receita: ${formatCurrency(month.revenue)}`}
                                                                />
                                                                {/* Net Profit bar */}
                                                                <div 
                                                                    className={`w-3/4 rounded-sm min-h-[4px] ${month.net_profit >= 0 ? 'bg-rr-yellow-500' : 'bg-red-500'}`}
                                                                    style={{ 
                                                                        height: `${Math.max(4, (Math.abs(month.net_profit) / maxNetProfit) * 60)}px` 
                                                                    }}
                                                                    title={`Lucro Líquido: ${formatCurrency(month.net_profit)}`}
                                                                />
                                                            </div>
                                                            <div className="text-center">
                                                                <p className="text-[8px] lg:text-[10px] text-gray-500">{month.month_name}</p>
                                                                <p className={`text-[8px] lg:text-[10px] font-medium ${month.net_profit >= 0 ? 'text-rr-yellow-500' : 'text-red-500'}`}>
                                                                    {formatCurrency(month.net_profit)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="flex justify-center gap-6 mt-4 text-xs">
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 bg-blue-500/30 rounded" />
                                                <span className="text-gray-400">Receita</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 bg-rr-yellow-500 rounded" />
                                                <span className="text-gray-400">Lucro Líquido</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}
                </TabsContent>
            </Tabs>

            {/* Export Dialog */}
            <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                <DialogContent className="bg-[#121212] border-[#262626] sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white">Exportar Relatório</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-gray-400">Data Inicial</Label>
                            <Input
                                type="date"
                                value={exportStartDate}
                                onChange={(e) => setExportStartDate(e.target.value)}
                                className="bg-[#1a1a1a] border-[#262626] text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-400">Data Final</Label>
                            <Input
                                type="date"
                                value={exportEndDate}
                                onChange={(e) => setExportEndDate(e.target.value)}
                                className="bg-[#1a1a1a] border-[#262626] text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-gray-400">Formato</Label>
                            <Select value={exportFormat} onValueChange={setExportFormat}>
                                <SelectTrigger className="bg-[#1a1a1a] border-[#262626] text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#1a1a1a] border-[#262626]">
                                    <SelectItem value="csv" className="text-white">
                                        <div className="flex items-center gap-2">
                                            <FileSpreadsheet className="w-4 h-4" />
                                            CSV (Excel)
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="json" className="text-white">
                                        <div className="flex items-center gap-2">
                                            <FileText className="w-4 h-4" />
                                            JSON
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExportDialogOpen(false)} className="border-[#262626] text-gray-400">
                            Cancelar
                        </Button>
                        <Button onClick={handleExport} disabled={exporting} className="bg-rr-yellow-500 text-black hover:bg-rr-yellow-600">
                            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                            Exportar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Products Report Dialog */}
            <Dialog open={productsDialogOpen} onOpenChange={setProductsDialogOpen}>
                <DialogContent className="bg-[#121212] border-[#262626] max-w-4xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Package className="w-5 h-5 text-rr-yellow-500" />
                            Relatório por Produto
                        </DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="h-[60vh]">
                        <div className="space-y-2 pr-4">
                            {/* Header */}
                            <div className="grid grid-cols-5 gap-2 p-3 bg-[#1a1a1a] rounded-md text-xs text-gray-400 font-medium sticky top-0">
                                <div>Produto</div>
                                <div className="text-right">Qtd</div>
                                <div className="text-right">Receita</div>
                                <div className="text-right">Custo</div>
                                <div className="text-right">Lucro</div>
                            </div>
                            {/* Rows */}
                            {productsReport?.products?.map((product, idx) => (
                                <div key={idx} className="grid grid-cols-5 gap-2 p-3 bg-[#121212] rounded-md text-sm">
                                    <div className="text-white font-medium truncate">{product.name}</div>
                                    <div className="text-right text-gray-400">{product.quantity_sold}</div>
                                    <div className="text-right text-white">{formatCurrency(product.revenue)}</div>
                                    <div className="text-right text-red-400">{formatCurrency(product.cost)}</div>
                                    <div className="text-right">
                                        <span className="text-emerald-500 font-bold">{formatCurrency(product.profit)}</span>
                                        <span className="text-xs text-gray-500 ml-1">({product.profit_margin_pct}%)</span>
                                    </div>
                                </div>
                            ))}
                            {/* Totals */}
                            {productsReport?.totals && (
                                <div className="grid grid-cols-5 gap-2 p-3 bg-rr-yellow-500/10 rounded-md text-sm font-bold border border-rr-yellow-500/20">
                                    <div className="text-rr-yellow-500">TOTAL</div>
                                    <div></div>
                                    <div className="text-right text-white">{formatCurrency(productsReport.totals.revenue)}</div>
                                    <div className="text-right text-red-400">{formatCurrency(productsReport.totals.cost)}</div>
                                    <div className="text-right text-emerald-500">{formatCurrency(productsReport.totals.profit)}</div>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FinancialPage;
